import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Team, MatchType, TossChoice, ValidationError } from '@/types';
import { validateMatchSetup } from '@/lib/validation';
import { PersistedMatchRef } from '@/lib/persistence';
import { useAuth } from '@/lib/auth';
import AppHeader from '@/components/AppHeader';

interface MatchSetupProps {
  teams: Team[];
  onStartMatch: (data: {
    matchNumber: number;
    date: string;
    time: string;
    matchType: MatchType;
    teamAId: string;
    teamBId: string;
    tossWonById: string;
    tossChoice: TossChoice;
    tournamentId: string;
    matchId?: string;
  }) => Promise<void>;
  recoveryRef: PersistedMatchRef | null;
  onRecoverMatch: (matchId?: string) => Promise<void>;
  onDiscardMatch: () => void;
  recoveryLoading: boolean;
  onSignOut: () => void;
  tournamentId?: string;
  tournamentName?: string;
  scheduledMatchId?: string;
  scheduledTeamAId?: string;
  scheduledTeamBId?: string;
  scheduledMatchNumber?: number;
  scheduledMatchType?: MatchType;
  scheduledDate?: string;
  scheduledTime?: string | null;
}

export default function MatchSetup({ teams, onStartMatch, recoveryRef, onRecoverMatch, onDiscardMatch, recoveryLoading, onSignOut, tournamentId, tournamentName, scheduledMatchId, scheduledTeamAId, scheduledTeamBId, scheduledMatchNumber, scheduledMatchType, scheduledDate, scheduledTime }: MatchSetupProps) {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const isStartingScheduled = !!scheduledMatchId;

  useEffect(() => {
    if (!tournamentId) {
      navigate('/tournaments', { replace: true });
    }
  }, [tournamentId, navigate]);

  const [matchNumber, setMatchNumber] = useState<number>(scheduledMatchNumber ?? 1);
  const [date, setDate] = useState<string>(scheduledDate ?? new Date().toISOString().split('T')[0]);
  const [time, setTime] = useState<string>(scheduledTime ?? '10:00');
  const [matchType, setMatchType] = useState<MatchType>(scheduledMatchType ?? 'league');
  const [teamAId, setTeamAId] = useState<string>(scheduledTeamAId ?? '');
  const [teamBId, setTeamBId] = useState<string>(scheduledTeamBId ?? '');
  const [tossWonById, setTossWonById] = useState<string>('');
  const [tossChoice, setTossChoice] = useState<TossChoice | ''>('');
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const selectedTeamA = useMemo(() => teams.find(t => t.id === teamAId), [teams, teamAId]);
  const selectedTeamB = useMemo(() => teams.find(t => t.id === teamBId), [teams, teamBId]);

  const recoveryTeamA = useMemo(() => teams.find(t => t.id === recoveryRef?.teamAId), [teams, recoveryRef]);
  const recoveryTeamB = useMemo(() => teams.find(t => t.id === recoveryRef?.teamBId), [teams, recoveryRef]);

  const firstServingTeam = useMemo(() => {
    if (!tossWonById || !tossChoice || !teamAId || !teamBId) return null;
    if (tossChoice === 'serve') {
      return teams.find(t => t.id === tossWonById);
    } else {
      return teams.find(t => t.id === (tossWonById === teamAId ? teamBId : teamAId));
    }
  }, [tossWonById, tossChoice, teamAId, teamBId, teams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateMatchSetup({
      matchNumber,
      date,
      matchType,
      teamAId,
      teamBId,
      tossWonById,
      tossChoice: tossChoice || null,
    });

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (!tournamentId) {
      navigate('/tournaments', { replace: true });
      return;
    }

    setSubmitting(true);
    try {
      await onStartMatch({
        matchNumber,
        date,
        time,
        matchType,
        teamAId,
        teamBId,
        tossWonById,
        tossChoice: tossChoice as TossChoice,
        tournamentId,
        matchId: scheduledMatchId,
      });
    } catch {
      setErrors([{
        id: 'submit-error',
        title: 'Failed to Start Match',
        message: 'Please check your connection and try again.',
      }]);
    } finally {
      setSubmitting(false);
    }
  };

  const getTeamError = (field: string): string | undefined => {
    const error = errors.find(e => e.message.toLowerCase().includes(field.toLowerCase()));
    return error?.message;
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* App Header */}
      <AppHeader onSignOut={onSignOut} tournamentId={tournamentId} tournamentName={tournamentName} />

      {/* Active Match Recovery Banner */}
      {recoveryRef && recoveryTeamA && recoveryTeamB && (
        <div className="bg-warning/10 border-b border-warning/30 px-4 py-4">
          <div className="max-w-md mx-auto">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-3 h-3 rounded-full bg-warning animate-pulse" />
              <h2 className="text-sm font-bold text-warning uppercase tracking-wider">
                Active Match Found
              </h2>
            </div>
            <div className="bg-surface rounded-lg border border-border p-3 mb-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-text-muted uppercase">Match</span>
                <span className="text-sm font-bold text-text-primary">#{recoveryRef.matchId.slice(0, 8)}</span>
              </div>
              <div className="flex items-center justify-center gap-3 py-2">
                <div className="text-center">
                  <div className="w-3 h-3 rounded-full mx-auto mb-1" style={{ backgroundColor: recoveryTeamA.color }} />
                  <span className="text-sm font-semibold" style={{ color: recoveryTeamA.color }}>
                    {recoveryTeamA.shortName}
                  </span>
                </div>
                <span className="text-text-muted text-xs font-bold">VS</span>
                <div className="text-center">
                  <div className="w-3 h-3 rounded-full mx-auto mb-1" style={{ backgroundColor: recoveryTeamB.color }} />
                  <span className="text-sm font-semibold" style={{ color: recoveryTeamB.color }}>
                    {recoveryTeamB.shortName}
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-text-muted uppercase">Status</span>
                <span className="text-xs font-semibold text-warning uppercase">{recoveryRef.status.replace('-', ' ')}</span>
              </div>
            </div>
            <div className={`grid gap-2 ${isAdmin ? 'grid-cols-2' : 'grid-cols-1'}`}>
              <button
                onClick={() => onRecoverMatch()}
                disabled={recoveryLoading}
                className="py-3 px-4 rounded-lg font-bold text-sm bg-success text-white transition-all active:scale-[0.98] disabled:opacity-50"
              >
                {recoveryLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    LOADING...
                  </span>
                ) : 'RESUME MATCH'}
              </button>
              {isAdmin && (
                <button
                  onClick={onDiscardMatch}
                  disabled={recoveryLoading}
                  className="py-3 px-4 rounded-lg font-bold text-sm bg-surface border border-error text-error transition-all active:scale-[0.98] disabled:opacity-50"
                >
                  DISCARD MATCH
                </button>
              )}
            </div>
            <button
              onClick={() => navigate(isAdmin ? '/tournaments' : '/scorer')}
              disabled={recoveryLoading}
              className="w-full mt-2 py-2 px-4 rounded-lg font-semibold text-xs bg-surface border border-border text-text-secondary transition-all active:scale-[0.98] disabled:opacity-50"
            >
              {isAdmin ? 'GO TO DASHBOARD' : 'BACK TO DASHBOARD'}
            </button>
          </div>
        </div>
      )}

      {/* Form Content */}
      <div className="flex-1 overflow-y-auto px-4 py-4 safe-area-bottom">
        <form onSubmit={handleSubmit} className="space-y-4 max-w-md mx-auto">
          {/* Tournament (fixed, cannot be changed) */}
          <section className="card">
            <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-4">
              TOURNAMENT
            </h2>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-surface-raised border border-border">
              <svg className="w-5 h-5 text-success flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 4h14" /><path d="M5 4v6a7 7 0 0 0 14 0V4" />
                <line x1="12" y1="16" x2="12" y2="20" /><line x1="9" y1="20" x2="15" y2="20" />
              </svg>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-text-primary truncate">
                  {tournamentName || 'Tournament'}
                </p>
                <p className="text-[11px] text-text-muted">Locked — match belongs to this tournament</p>
              </div>
            </div>
          </section>

          {/* Scheduled Match Banner */}
          {isStartingScheduled && (
            <div className="bg-info/10 border border-info/30 rounded-lg p-3 flex items-center gap-3">
              <svg className="w-5 h-5 text-info flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
              </svg>
              <p className="text-xs text-info font-semibold">Starting scheduled match — match details are locked. Select toss to begin.</p>
            </div>
          )}

          {/* Match Details Section */}
          <section className="card">
            <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-4">
              MATCH DETAILS
            </h2>

            {/* Match Number */}
            <div className="mb-4">
              <label className="block text-sm text-text-secondary mb-2">Match Number</label>
              <input
                type="number"
                inputMode="numeric"
                value={matchNumber || ''}
                onChange={(e) => setMatchNumber(parseInt(e.target.value) || 0)}
                disabled={isStartingScheduled}
                className={`input-field text-lg ${
                  getTeamError('match number') ? 'border-error' : ''
                }`}
                placeholder="1"
              />
              {getTeamError('match number') && (
                <p className="text-error text-sm mt-1">{getTeamError('match number')}</p>
              )}
            </div>

            {/* Date */}
            <div className="mb-4">
              <label className="block text-sm text-text-secondary mb-2">Date</label>
              <div className="relative">
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  disabled={isStartingScheduled}
                  className="input-field text-lg w-full"
                />
              </div>
            </div>

            {/* Time */}
            <div className="mb-4">
              <label className="block text-sm text-text-secondary mb-2">Time</label>
              <div className="relative">
                <input
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  disabled={isStartingScheduled}
                  className="input-field text-lg w-full"
                />
              </div>
            </div>

            {/* Match Type */}
            <div>
              <label className="block text-sm text-text-secondary mb-2">Match Type</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setMatchType('league')}
                  disabled={isStartingScheduled}
                  className={`py-3 px-4 rounded-lg font-semibold transition-all ${
                    matchType === 'league'
                      ? 'bg-team-a text-white'
                      : 'bg-surface-raised text-text-secondary border border-border'
                  }`}
                >
                  LEAGUE
                </button>
                <button
                  type="button"
                  onClick={() => setMatchType('playoff')}
                  disabled={isStartingScheduled}
                  className={`py-3 px-4 rounded-lg font-semibold transition-all ${
                    matchType === 'playoff'
                      ? 'bg-team-b text-white'
                      : 'bg-surface-raised text-text-secondary border border-border'
                  }`}
                >
                  PLAYOFF
                </button>
              </div>
              <p className="text-text-muted text-xs mt-2">
                Best of {matchType === 'league' ? '3' : '5'} Sets
              </p>
            </div>
          </section>

          {/* Teams Section */}
          <section className="card">
            <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-4">
              TEAMS
            </h2>

            {/* Team A */}
            <div className="mb-4">
              <label className="block text-sm text-text-secondary mb-2">Team A</label>
              <select
                value={teamAId}
                onChange={(e) => {
                  setTeamAId(e.target.value);
                  if (teamBId === e.target.value) setTeamBId('');
                  if (tossWonById === e.target.value) setTossWonById('');
                }}
                disabled={isStartingScheduled}
                className={`input-field text-lg ${
                  getTeamError('Team A') ? 'border-error' : ''
                }`}
              >
                <option value="">Select Team A</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
              {getTeamError('Team A') && (
                <p className="text-error text-sm mt-1">{getTeamError('Team A')}</p>
              )}
            </div>

            {/* Team B */}
            <div className="">
              <label className="block text-sm text-text-secondary mb-2">Team B</label>
              <select
                value={teamBId}
                onChange={(e) => {
                  setTeamBId(e.target.value);
                  if (tossWonById === e.target.value && tossWonById === teamAId) {
                    setTossWonById('');
                  }
                }}
                disabled={isStartingScheduled || !teamAId}
                className={`input-field text-lg ${
                  getTeamError('Team B') ? 'border-error' : ''
                }`}
              >
                <option value="">Select Team B</option>
                {teams
                  .filter((t) => t.id !== teamAId)
                  .map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
              </select>
              {getTeamError('Team B different') && (
                <p className="text-error text-sm mt-1">{getTeamError('Team B different')}</p>
              )}
            </div>
          </section>

          {/* Toss Section */}
          <section className="card">
            <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-4">
              TOSS
            </h2>

            {/* Toss Won By */}
            <div className="mb-4">
              <label className="block text-sm text-text-secondary mb-2">Toss Won By</label>
              <div className="space-y-2">
                {selectedTeamA && (
                  <button
                    type="button"
                    onClick={() => setTossWonById(teamAId)}
                    className={`w-full py-3 px-4 rounded-lg font-semibold transition-all flex items-center gap-3 ${
                      tossWonById === teamAId
                        ? 'bg-team-a text-white'
                        : 'bg-surface-raised text-text-secondary border border-border'
                    }`}
                  >
                    <span
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: selectedTeamA.color }}
                    />
                    {selectedTeamA.name}
                  </button>
                )}
                {selectedTeamB && (
                  <button
                    type="button"
                    onClick={() => setTossWonById(teamBId)}
                    className={`w-full py-3 px-4 rounded-lg font-semibold transition-all flex items-center gap-3 ${
                      tossWonById === teamBId
                        ? 'bg-team-b text-white'
                        : 'bg-surface-raised text-text-secondary border border-border'
                    }`}
                  >
                    <span
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: selectedTeamB.color }}
                    />
                    {selectedTeamB.name}
                  </button>
                )}
                {!selectedTeamA && !selectedTeamB && (
                  <p className="text-text-muted text-sm py-3">
                    Select both teams first
                  </p>
                )}
              </div>
              {getTeamError('toss') && (
                <p className="text-error text-sm mt-2">{getTeamError('toss')}</p>
              )}
            </div>

            {/* They Choose To */}
            {tossWonById && (
              <div className="mb-4">
                <label className="block text-sm text-text-secondary mb-2">They Choose To</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setTossChoice('serve')}
                    className={`py-3 px-4 rounded-lg font-semibold transition-all ${
                      tossChoice === 'serve'
                        ? 'bg-serving text-white'
                        : 'bg-surface-raised text-text-secondary border border-border'
                    }`}
                  >
                    SERVE
                  </button>
                  <button
                    type="button"
                    onClick={() => setTossChoice('side')}
                    className={`py-3 px-4 rounded-lg font-semibold transition-all ${
                      tossChoice === 'side'
                        ? 'bg-info text-white'
                        : 'bg-surface-raised text-text-secondary border border-border'
                    }`}
                  >
                    SIDE
                  </button>
                </div>
              </div>
            )}

            {/* Derived Info */}
            {firstServingTeam && (
              <div className="bg-surface-raised rounded-lg p-3 border border-border">
                <p className="text-sm text-text-secondary">
                  <span className="text-text-muted">ⓘ</span>{' '}
                  <span className="font-semibold">{firstServingTeam.name}</span> serve first
                </p>
              </div>
            )}
          </section>

          {/* Start Match Button */}
          <button
            type="submit"
            disabled={submitting}
            className={`btn-primary bg-team-a text-white font-bold mt-6 ${
              submitting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                STARTING...
              </span>
            ) : (
              'START MATCH'
            )}
          </button>

          {/* Global Errors */}
          {errors.length > 0 && errors[0].title === 'Failed to Start Match' && (
            <div className="bg-error/20 border border-error rounded-lg p-4 mt-4">
              <p className="text-error font-semibold">{errors[0].title}</p>
              <p className="text-error/80 text-sm mt-1">{errors[0].message}</p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
